import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Search } from "lucide-react";
import { blogPillars, blogPosts as mockPosts } from "../mock/mock";
import { getBlog } from "../lib/api";

export default function Blog() {
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");
  const [blogPosts, setBlogPosts] = useState(mockPosts);

  useEffect(() => {
    getBlog()
      .then((data) => { if (Array.isArray(data) && data.length) setBlogPosts(data); })
      .catch(() => {});
  }, []);

  const filtered = blogPosts.filter((p) => {
    const matchPillar = active === "all" || p.pillar === active;
    const matchQuery = p.title.toLowerCase().includes(query.toLowerCase());
    return matchPillar && matchQuery;
  });

  return (
    <div>
      {/* Hero */}
      <section className="max-w-4xl mx-auto px-5 pt-16 pb-10 text-center">
        <p className="font-ui tracking-[0.3em] text-teal text-sm font-semibold uppercase">The Blog</p>
        <h1 className="font-serif-display text-5xl md:text-6xl font-bold text-navy mt-4">Real talk, in writing.</h1>
        <p className="font-ui text-navy/70 mt-5 text-lg leading-relaxed max-w-2xl mx-auto">
          Organized into five content pillars so you can go deep on exactly what you're wrestling with — from founder
          burnout to pricing confidence.
        </p>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-5 py-10">
        <h2 className="font-serif-display text-3xl font-bold text-navy mb-8">Explore by pillar</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {blogPillars.map((p) => (
            <button
              key={p.slug}
              onClick={() => setActive(active === p.slug ? "all" : p.slug)}
              className={`lift text-left rounded-xl overflow-hidden relative h-52 flex flex-col justify-end ${active === p.slug ? "ring-4 ring-offset-2 ring-teal" : ""}`}
            >
              <img src={p.img} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${p.color}f2, ${p.color}66 50%, ${p.color}22)` }} />
              <div className="relative p-4">
                <h3 className="font-serif-display text-white text-lg font-bold leading-tight">{p.title}</h3>
                <p className="font-ui text-white/85 text-xs mt-2 leading-snug line-clamp-3">{p.blurb}</p>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Filter bar */}
      <section className="max-w-6xl mx-auto px-5 pt-8">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between border-b pb-5" style={{ borderColor: "var(--cream-dark)" }}>
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => setActive("all")} className={`px-4 py-2 rounded-full font-ui text-sm font-medium transition-colors ${active === "all" ? "bg-navy text-cream" : "bg-white text-navy border"}`} style={active !== "all" ? { borderColor: "var(--cream-dark)" } : {}}>All Posts</button>
            {active !== "all" && (
              <span className="font-ui text-sm text-navy/60">Filtered by <b className="text-navy">{blogPillars.find((p) => p.slug === active)?.title}</b></span>
            )}
          </div>
          <div className="relative">
            <Search size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles…"
              className="pl-10 pr-4 py-2.5 rounded-full bg-white border font-ui text-sm outline-none focus:ring-2 ring-teal w-full sm:w-64"
              style={{ borderColor: "var(--cream-dark)" }}
            />
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="max-w-6xl mx-auto px-5 py-12">
        {filtered.length === 0 ? (
          <p className="text-center font-ui text-navy/60 py-16">No articles match your search yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="lift group bg-white rounded-2xl overflow-hidden border flex flex-col" style={{ borderColor: "var(--cream-dark)" }}>
                <div className="h-48 overflow-hidden">
                  <img src={post.img} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="font-ui text-xs font-semibold text-teal uppercase tracking-wider">{post.pillarLabel}</span>
                  <h3 className="font-serif-display text-2xl font-semibold text-navy mt-2 leading-tight group-hover:text-orange transition-colors">{post.title}</h3>
                  <p className="font-ui text-navy/70 text-sm mt-3 leading-relaxed flex-1">{post.excerpt}</p>
                  <div className="flex items-center justify-between mt-5 font-ui text-xs text-navy/50">
                    <span>{post.author}</span>
                    <span>{post.date} · {post.readTime}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* AI-search note callout */}
      <section className="max-w-4xl mx-auto px-5 pb-8">
        <div className="bg-navy text-cream rounded-2xl p-8 flex gap-5 items-start">
          <Sparkles size={28} className="text-teal shrink-0 mt-1" />
          <div>
            <h3 className="font-serif-display text-2xl font-semibold">Built to be found — by people and AI.</h3>
            <p className="font-ui text-white/80 mt-2 leading-relaxed">
              Every article opens with a concise, direct answer to the question it addresses, then links back to its
              content pillar. That's how you build authority for both traditional and AI-powered search.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
